import { memo, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiInputTextbox } from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { convertTimeToFullShortHand } from "@miplatform/utils";
import { mdiPencil, mdiUndo } from "@mdi/js";
import { useProfile } from "@mibuildweb/hooks";
import { Box, Typography } from "@mui/material";

const EditableNoteDetail = memo(
  ({
    detailField,
    edit,
    disabled = false,
    completed,
    historyState = {},
    isShowOriginalDisabled = false,
  }) => {
    const { profile } = useProfile();
    const [isEditing, setIsEditing] = useState(false);
    const [showOriginal, setShowOriginal] = useState(false);
    const [changeInfo, setChangeInfo] = useState(null);
    const currentValue = edit?.getValue?.(detailField.name) ?? "";
    const initialValue = edit?.getInitialValue?.(detailField.name) ?? "";

    const { classes, theme } = useStyles({
      isEditing,
      minRows: detailField.minRows,
    });

    const { state = "default", technicianNote, isDispatchEdit } = historyState;
    const originalNote = technicianNote?.note ?? initialValue;
    const displayValue = String(showOriginal ? originalNote : currentValue);
    const hasUnsavedChange = edit?.isDirtyField?.(detailField.name) ?? false;
    const canShowOriginal =
      (isDispatchEdit || hasUnsavedChange) && !isShowOriginalDisabled;

    useEffect(() => {
      const latestNote = historyState?.latestNote;

      if (!latestNote?.createdByUser) {
        return;
      }

      setChangeInfo({
        changedAt: latestNote.createdOn,
        changedBy: latestNote.createdByUser.displayName || "Unknown",
      });
    }, [
      historyState?.latestNote?.createdByUser?.displayName,
      historyState?.latestNote?.createdOn,
    ]);

    const startEditing = () => {
      setShowOriginal(false);
      setIsEditing(true);
    };

    const cancelEditing = () => {
      edit?.update?.({ [detailField.name]: initialValue });
      setShowOriginal(false);
      setIsEditing(false);
    };

    const completeEditing = () => {
      const changed = edit?.isDirtyField?.(detailField.name) ?? false;

      setChangeInfo(
        changed
          ? {
              changedAt: new Date().toISOString(),
              changedBy:
                profile?.displayName ||
                `${profile?.firstName || ""} ${
                  profile?.lastName || ""
                }`.trim() ||
                "You",
            }
          : null
      );
      setShowOriginal(false);
      setIsEditing(false);
    };

    const revertToOriginal = () => {
      setShowOriginal((prev) => !prev);
    };

    let stateClass = "";
    if (!showOriginal) {
      if (state === "warning" || hasUnsavedChange) {
        stateClass = classes.changedRoot;
      } else if (completed) {
        stateClass = classes.completedRoot;
      }
    }

    if (isEditing) {
      return (
        <Box className={classes.editingBox}>
          <MiInputTextbox
            name={detailField.name}
            label={detailField.label}
            value={currentValue}
            multiline={detailField.multiline}
            rows={detailField.rows}
            minRows={detailField.minRows}
            maxRows={detailField.maxRows}
            disabled={disabled}
            required={detailField.required}
            handleChange={(event) =>
              edit?.update?.({ [detailField.name]: event.target.value })
            }
            className={classes.editInput}
          />
          <Box className={classes.formAction}>
            <MiButton
              title="Cancel"
              variant={BUTTON_VARIANTS.TEXT}
              onClick={cancelEditing}
              className={classes.editButton}
            />
            <MiButton
              title="Done"
              variant={BUTTON_VARIANTS.CONTAINED}
              onClick={completeEditing}
              style={{
                padding: theme.spacing(1, 2),
                marginLeft: theme.spacing(1),
                color: theme.palette.common.white,
                textDecoration: "none",
                fontSize: 12,
              }}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box className={`${classes.root} ${stateClass}`}>
        <Typography className={classes.label}>
          {detailField.label}
          <span style={{ color: theme.palette.error.main }}>
            {detailField.required && " *"}
          </span>
        </Typography>
        <Typography className={classes.value}>
          {displayValue || "--"}
        </Typography>
        <Box className={classes.editActions}>
          <Box className={classes.changeMetadata}>
            <Typography className={classes.changedBy}>
              {changeInfo?.changedBy || ""}
            </Typography>
            <Typography className={classes.changedAt}>
              {changeInfo?.changedAt
                ? convertTimeToFullShortHand(changeInfo.changedAt)
                : ""}
            </Typography>
          </Box>

          <Box className={classes.editActionsButtons}>
            {canShowOriginal && (
              <MiButton
                title={showOriginal ? "Current Note" : "Original Note"}
                variant={BUTTON_VARIANTS.TEXT}
                className={classes.editButton}
                color={theme.palette.grey.dark}
                onClick={revertToOriginal}
                startIcon={{
                  path: mdiUndo,
                  iconColor: theme.palette.common.black,
                  iconSize: 0.6,
                }}
              />
            )}
            {!disabled && (
              <MiButton
                title="Edit Note"
                variant={BUTTON_VARIANTS.TEXT}
                className={classes.editButton}
                onClick={startEditing}
                startIcon={{
                  path: mdiPencil,
                  iconColor: theme.palette.common.black,
                  iconSize: 0.6,
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  }
);

export default EditableNoteDetail;

const useStyles = makeStyles()((theme, { isEditing, minRows = 2 }) => ({
  root: {
    position: "relative",
    minHeight: minRows * 30,
    padding: 6,
    border: `1px solid ${theme.palette.grey.darkerAccent}`,
    borderRadius: 4,
    backgroundColor: theme.palette.grey.lightAccent,
  },
  changedRoot: {
    border: `1px solid ${theme.palette.orange.main} !important`,
    backgroundColor: `${theme.palette.orange.light} !important`,
  },
  completedRoot: {
    border: `1px solid ${theme.palette.success.main} !important`,
    backgroundColor: `${theme.palette.success.light} !important`,
  },
  label: {
    color: theme.palette.grey.darkerAccent,
    fontSize: 11,
    lineHeight: 1.2,
  },
  value: {
    marginTop: theme.spacing(1),
    paddingBottom: theme.spacing(5),
    color: "#212121",
    fontSize: 12,
    lineHeight: 1.35,
    whiteSpace: "pre-wrap",
  },
  editButton: {
    padding: 0,
    color: theme.palette.common.black,
    fontSize: 12,
    textDecoration: "underline",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
    },
  },
  editActionsButtons: {
    display: "flex",
    gap: theme.spacing(2),
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  changeMetadata: {
    position: "absolute",
    left: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  changedBy: {
    color: theme.palette.grey.medium,
    fontSize: 12,
  },
  changedAt: {
    color: theme.palette.grey.darkerAccent,
    fontSize: 12,
  },
  editingBox: {
    padding: isEditing ? 0 : theme.spacing(2),
    borderRadius: 4,
    backgroundColor: !isEditing ? theme.palette.grey.light : "inherit",
  },
  editInput: {
    width: "100%",
    backgroundColor: theme.palette.grey.mediumLight,
    "& .MuiInputBase-root": {
      fontSize: 12,
    },
    "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
      fontSize: 12,
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
      fontSize: 12,
    },
  },
  editActions: {
    position: "absolute",
    bottom: 2,
    left: 6,
    right: 6,
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    alignItems: "center",
    minHeight: 22,
  },
  formAction: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
}));
